import React, { useState, useEffect } from 'react';
import { Camera, User, Mail, Phone, Building, Save, X, Globe } from 'lucide-react';
import { uploadToS3 } from '../../utils/api';
import { getCurrentUser, fetchUserAttributes, fetchAuthSession } from 'aws-amplify/auth';

export function ProfileView() {
  const [isUploading, setIsUploading] = useState(false);
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    company_website: '',
    bio: ''
  });
  const [accountType, setAccountType] = useState('User');
  const [memberSince, setMemberSince] = useState('');
  const [userGroups, setUserGroups] = useState<string[]>([]);
  const [userTags, setUserTags] = useState<string[]>([]);

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;

      // Get Cognito groups
      const groups = (idToken?.payload['cognito:groups'] as string[]) || [];
      setUserGroups(groups);

      // Determine account type based on groups and tags
      let roleLabel = 'User';
      if (groups.includes('super_admin')) {
        roleLabel = 'Super Admin';
      } else if (groups.includes('admin_manager')) {
        roleLabel = 'Admin Manager';
      } else if (groups.includes('admin')) {
        roleLabel = 'Admin';
      }

      // Get profile from backend (includes profile_image_url, tags, created_at)
      const response = await fetch(`${import.meta.env.VITE_API_URL}/profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken?.toString()}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const profile = await response.json();

        // Set profile image if exists (prefer presigned URL)
        if (profile.profile_image_url_presigned) {
          setProfileImageUrl(profile.profile_image_url_presigned);
        } else if (profile.profile_image_url) {
          setProfileImageUrl(profile.profile_image_url);
        }

        // Get tags from profile
        const tags = profile.tags || [];
        setUserTags(tags);

        // Override role label if user is not admin but has group tags
        if (!groups.includes('super_admin') && !groups.includes('admin') && !groups.includes('admin_manager')) {
          // Check tags - prioritize DFG over BPG
          if (tags.includes('DFPB')) {
            roleLabel = 'Debt Free Property Blueprint Member';
          } else if (tags.includes('BPG')) {
            roleLabel = 'Blueprint Group Member';
          }
        }

        setAccountType(roleLabel);

        // Parse member since date from created_at
        if (profile.created_at) {
          const date = new Date(profile.created_at);
          setMemberSince(date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }));
        }

        // Parse name from profile or email
        const email = profile.email || '';
        const name = profile.name || email.split('@')[0];
        const [firstName = '', lastName = ''] = name.split(/[ ._-]/);

        setFormData({
          firstName: profile.firstName || firstName.charAt(0).toUpperCase() + firstName.slice(1),
          lastName: profile.lastName || (lastName ? lastName.charAt(0).toUpperCase() + lastName.slice(1) : ''),
          email: email,
          phone: profile.phone || '',
          company: profile.company || '',
          company_website: profile.company_website || '',
          bio: profile.bio || ''
        });
      } else {
        console.error('Failed to load profile:', response.status);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be less than 5MB');
      return;
    }

    try {
      setIsUploading(true);
      const fileUrl = await uploadToS3(file, 'profile-pictures');
      setProfileImageUrl(fileUrl);

      // Save profile image URL to backend
      const response = await fetch(`${import.meta.env.VITE_API_URL}/profile`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${(await fetchAuthSession()).tokens?.idToken?.toString()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ profile_image_url: fileUrl })
      });

      if (!response.ok) {
        throw new Error('Failed to save profile image');
      }

      console.log('Profile image saved successfully');

      // Dispatch event to update profile image in header immediately
      window.dispatchEvent(new CustomEvent('profile-image-updated', { detail: fileUrl }));
    } catch (error) {
      console.error('Error uploading profile image:', error);
      alert('Failed to upload image. Please try again.');
      setProfileImageUrl(null); // Reset on error
    } finally {
      setIsUploading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSave = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/profile`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${(await fetchAuthSession()).tokens?.idToken?.toString()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          company: formData.company,
          company_website: formData.company_website,
          bio: formData.bio
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save profile');
      }

      alert('Profile saved successfully!');
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Failed to save profile. Please try again.');
    }
  };

  // Get initials for default avatar
  const firstInitial = formData.firstName?.[0] || '';
  const lastInitial = formData.lastName?.[0] || '';
  const initials = `${firstInitial}${lastInitial}`.toUpperCase() || '?';

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-tdb-primary">My Profile</h1>
        <p className="text-gray-600 mt-1">Manage your personal information and profile picture</p>
      </div>

      {/* Profile Card */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        {/* Header Background */}
        <div className="h-32 bg-gradient-to-r from-tdb-primary to-tdb-secondary"></div>

        <div className="px-8 pb-8">
          {/* Profile Picture */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between -mt-16 mb-8">
            <div className="relative inline-block">
              <div className="relative">
                {profileImageUrl ? (
                  <img
                    src={profileImageUrl}
                    alt="Profile"
                    className="w-32 h-32 rounded-full border-4 border-white shadow-xl object-cover"
                  />
                ) : (
                  <div className="w-32 h-32 rounded-full border-4 border-white shadow-xl bg-tdb-accent flex items-center justify-center">
                    <span className="text-4xl font-bold text-white">{initials}</span>
                  </div>
                )}

                <label
                  htmlFor="profile-image-upload"
                  className="absolute bottom-0 right-0 bg-tdb-accent hover:bg-[#0ea5ef] text-white p-3 rounded-full cursor-pointer shadow-lg transition-all transform hover:scale-110"
                >
                  {isUploading ? (
                    <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                  ) : (
                    <Camera className="w-5 h-5" />
                  )}
                  <input
                    id="profile-image-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={isUploading}
                  />
                </label>
              </div>

              <div className="mt-4">
                <h2 className="text-2xl font-bold text-tdb-primary">
                  {formData.firstName} {formData.lastName}
                </h2>
                <p className="text-gray-600">{formData.email}</p>
              </div>
            </div>

            <div className="mt-4 md:mt-0">
              <button
                onClick={handleSave}
                className="flex items-center space-x-2 px-6 py-3 bg-tdb-accent text-white rounded-xl hover:bg-[#0ea5ef] transition-all shadow-lg hover:shadow-xl font-bold"
              >
                <Save className="w-5 h-5" />
                <span>Save Changes</span>
              </button>
            </div>
          </div>

          {/* Profile Form */}
          <div className="space-y-6">
            {/* Personal Information */}
            <div>
              <h3 className="text-lg font-bold text-tdb-primary mb-4 flex items-center space-x-2">
                <User className="w-5 h-5 text-tdb-accent" />
                <span>Personal Information</span>
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-tdb-primary mb-2">
                    First Name
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-tdb-primary mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
                  />
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div>
              <h3 className="text-lg font-bold text-tdb-primary mb-4 flex items-center space-x-2">
                <Mail className="w-5 h-5 text-tdb-accent" />
                <span>Contact Information</span>
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-tdb-primary mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    disabled
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl bg-gray-100 text-gray-500 cursor-not-allowed"
                    title="Email cannot be changed"
                  />
                  <p className="text-xs text-gray-500 mt-1">Email address cannot be changed</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-tdb-primary mb-2">
                    Phone Number
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-tdb-primary mb-2">
                    Company
                  </label>
                  <div className="relative">
                    <Building className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-tdb-primary mb-2">
                    Company Website
                  </label>
                  <div className="relative">
                    <Globe className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="url"
                      name="company_website"
                      value={formData.company_website}
                      onChange={handleInputChange}
                      placeholder="https://example.com"
                      className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Bio */}
            <div>
              <label className="block text-sm font-semibold text-tdb-primary mb-2">
                Bio
              </label>
              <textarea
                name="bio"
                value={formData.bio}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all resize-none"
                placeholder="Tell us about yourself..."
              />
              <p className="text-sm text-gray-500 mt-2">
                A brief description for your profile. Max 500 characters.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Account Information */}
      <div className="bg-white rounded-2xl shadow-lg p-8">
        <h3 className="text-lg font-bold text-tdb-primary mb-6">Account Information</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-4 bg-gray-50 rounded-xl">
            <div className="text-sm text-gray-600 mb-1">Account Type</div>
            <div className="text-lg font-bold text-tdb-primary">{accountType}</div>
          </div>

          <div className="p-4 bg-gray-50 rounded-xl">
            <div className="text-sm text-gray-600 mb-1">Member Since</div>
            <div className="text-lg font-bold text-tdb-primary">{memberSince || 'N/A'}</div>
          </div>
        </div>

        {/* Tags Display */}
        {userTags.length > 0 && (
          <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
            <div className="text-sm font-semibold text-blue-900 mb-2">User Tags</div>
            <div className="flex flex-wrap gap-2">
              {userTags.map(tag => (
                <span key={tag} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
